import Phaser from 'phaser';
import { TEXTURE_KEYS, DEPTHS, TILE_SIZE } from '../systems/util/constants.js';
import { EnemyStateMachine } from '../systems/ai/EnemyStateMachine.js';

// How far above a full tile's bottom edge a platform's actual drawn/collided
// underside sits — TILE_PLATFORM (textures.js) is only 40% of a tile tall
// and centered in its row, not a full-height tile. Used by ceilingMount
// enemies (the turret) to hang from the platform's real edge instead of an
// imaginary full-tile one. Kept in sync with textures.js/Game.js by hand —
// there isn't a single shared source for it since one's a texture-gen
// constant and the other is tile-placement code.
const CEILING_GAP = TILE_SIZE - (TILE_SIZE / 2 + (TILE_SIZE * 0.4) / 2);

// Comic-art overhaul: the supplied art only covers 3 soldier looks, 2 drone
// looks, 1 turret and 1 (mech) boss — the other variant configs that used
// to have their own procedural texture are commented out below rather than
// deleted, in case matching art shows up for them later. `scale` is new:
// each texture is a large hand-drawn image (100-200+ px) that needs scaling
// down to read at this game's small on-screen size, applied once via
// setScale() in the Enemy constructor. bodySize is authored in final
// on-screen (post-scale) pixels — the constructor divides by `scale` before
// calling Body.setSize, since that actually wants native pre-scale pixels.
//
// `contentBBox` ([x0,y0,x1,y1], measured via PIL getbbox() on each PNG)
// replaces what used to be a fixed-pixel `bodyOffset`. That was a real bug:
// a fixed offset only stays correctly positioned at the ONE scale it was
// tuned for — once `scale` got bumped several times (repeated "make it N%
// bigger" requests), the visible sprite kept growing around a hitbox
// pinned at its original small-scale position, so enemies drifted further
// and further from actually standing on the floor. Deriving the offset
// from the content box + current scale every time (see the constructor)
// keeps the hitbox correctly placed on the character regardless of scale.
export const ENEMY_CONFIGS = {
  'corp-soldier': {
    texture: TEXTURE_KEYS.SOLDIER,
    scale: 0.6417,
    hp: 3,
    gravity: true,
    stationary: false,
    moveSpeed: 55,
    patrolRange: 90,
    detectRange: 260,
    attackRange: 220,
    shootCooldown: 1900,
    projectileSpeed: 260,
    contactDamage: 1,
    // Height was 30 — barely past the ankles on a sprite that stands ~80px
    // tall, so the player's shots (which fire from roughly chest height,
    // see SHOOT_MUZZLE_NATIVE_ABOVE_FEET in Player.js) passed clean over
    // this hurtbox. Raised to cover torso height, capped under 64px (the
    // clearance under a TIER1 platform — see generate-chunks.mjs) since
    // this soldier's patrol range can carry it under one.
    bodySize: [20, 56],
    contentBBox: [16, 11, 101, 127],
    requiresLineOfSight: true
  },
  'corp-soldier-shield': {
    texture: TEXTURE_KEYS.SOLDIER_SHIELD,
    scale: 0.684,
    hp: 6,
    gravity: true,
    stationary: false,
    moveSpeed: 35,
    patrolRange: 70,
    detectRange: 220,
    attackRange: 180,
    shootCooldown: 2200,
    projectileSpeed: 240,
    contactDamage: 2,
    // See corp-soldier's comment — same too-short-to-be-shot hurtbox bug.
    bodySize: [22, 58],
    contentBBox: [5, 14, 78, 130],
    requiresLineOfSight: true
  },
  'corp-soldier-heavy': {
    texture: TEXTURE_KEYS.SOLDIER_HEAVY,
    scale: 0.7092,
    hp: 7,
    gravity: true,
    stationary: false,
    moveSpeed: 40,
    patrolRange: 70,
    detectRange: 260,
    attackRange: 240,
    shootCooldown: 900,
    projectileSpeed: 250,
    contactDamage: 2,
    // See corp-soldier's comment — same too-short-to-be-shot hurtbox bug.
    bodySize: [22, 58],
    contentBBox: [9, 10, 110, 126],
    requiresLineOfSight: true
  },

  /* Other soldier archetypes — no matching art in the comic-art pass; kept
   * for reference / in case new art arrives for them. Re-enable by giving
   * each a `texture` that points at a loaded key and a `scale`.
  'corp-soldier-scout': {
    texture: TEXTURE_KEYS.SOLDIER_SCOUT,
    hp: 2, gravity: true, stationary: false, moveSpeed: 85, patrolRange: 110,
    detectRange: 300, attackRange: 240, shootCooldown: 1500, projectileSpeed: 280,
    contactDamage: 1, bodySize: [16, 24], bodyOffset: [3, 5], requiresLineOfSight: true
  },
  'corp-soldier-recon': {
    texture: TEXTURE_KEYS.SOLDIER_RECON,
    hp: 3, gravity: true, stationary: false, moveSpeed: 50, patrolRange: 90,
    detectRange: 320, attackRange: 260, shootCooldown: 1700, projectileSpeed: 270,
    contactDamage: 1, bodySize: [16, 24], bodyOffset: [3, 5], requiresLineOfSight: true
  },
  'corp-soldier-assault': {
    texture: TEXTURE_KEYS.SOLDIER_ASSAULT,
    hp: 4, gravity: true, stationary: false, moveSpeed: 60, patrolRange: 90,
    detectRange: 240, attackRange: 200, shootCooldown: 1300, projectileSpeed: 280,
    contactDamage: 2, bodySize: [16, 24], bodyOffset: [3, 5], requiresLineOfSight: true
  },
  'corp-soldier-stealth': {
    texture: TEXTURE_KEYS.SOLDIER_STEALTH,
    hp: 2, gravity: true, stationary: false, moveSpeed: 70, patrolRange: 100,
    detectRange: 280, attackRange: 220, shootCooldown: 1600, projectileSpeed: 260,
    contactDamage: 1, bodySize: [16, 24], bodyOffset: [3, 5], requiresLineOfSight: true
  },
  'corp-soldier-elite': {
    texture: TEXTURE_KEYS.SOLDIER_ELITE,
    hp: 8, gravity: true, stationary: false, moveSpeed: 65, patrolRange: 90,
    detectRange: 280, attackRange: 240, shootCooldown: 1100, projectileSpeed: 300,
    contactDamage: 2, bodySize: [16, 24], bodyOffset: [3, 5], requiresLineOfSight: true
  },
  */

  'drone-basic': {
    texture: TEXTURE_KEYS.DRONE,
    scale: 0.725,
    hp: 2,
    gravity: false,
    stationary: false,
    moveSpeed: 75,
    patrolRange: 120,
    detectRange: 250,
    attackRange: 220,
    shootCooldown: 1400,
    projectileSpeed: 240,
    contactDamage: 1,
    bodySize: [26, 20],
    contentBBox: [10, 11, 94, 82],
    flightBob: true
  },
  'drone-elite': {
    texture: TEXTURE_KEYS.DRONE_ELITE,
    scale: 0.701,
    hp: 6,
    gravity: false,
    stationary: false,
    moveSpeed: 85,
    patrolRange: 120,
    detectRange: 280,
    attackRange: 240,
    shootCooldown: 1100,
    projectileSpeed: 280,
    contactDamage: 2,
    bodySize: [20, 22],
    contentBBox: [10, 16, 80, 89],
    flightBob: true
  },

  /* Other drone archetypes — no matching art in the comic-art pass; kept
   * for reference / in case new art arrives for them.
  'drone-heavy': {
    texture: TEXTURE_KEYS.DRONE_HEAVY,
    hp: 5, gravity: false, stationary: false, moveSpeed: 55, patrolRange: 100,
    detectRange: 230, attackRange: 200, shootCooldown: 1700, projectileSpeed: 220,
    contactDamage: 2, bodySize: [28, 20], bodyOffset: [3, 3], flightBob: true
  },
  'drone-scout': {
    texture: TEXTURE_KEYS.DRONE_SCOUT,
    hp: 1, gravity: false, stationary: false, moveSpeed: 110, patrolRange: 150,
    detectRange: 300, attackRange: 240, shootCooldown: 1200, projectileSpeed: 260,
    contactDamage: 1, bodySize: [18, 11], bodyOffset: [3, 3], flightBob: true
  },
  'drone-stealth': {
    texture: TEXTURE_KEYS.DRONE_STEALTH,
    hp: 2, gravity: false, stationary: false, moveSpeed: 95, patrolRange: 130,
    detectRange: 250, attackRange: 220, shootCooldown: 1300, projectileSpeed: 260,
    contactDamage: 1, bodySize: [22, 14], bodyOffset: [3, 3], flightBob: true
  },
  'drone-shield': {
    texture: TEXTURE_KEYS.DRONE_SHIELD,
    hp: 4, gravity: false, stationary: false, moveSpeed: 70, patrolRange: 110,
    detectRange: 250, attackRange: 220, shootCooldown: 1500, projectileSpeed: 230,
    contactDamage: 1, bodySize: [26, 14], bodyOffset: [4, 3], flightBob: true
  },
  'drone-hacker': {
    texture: TEXTURE_KEYS.DRONE_HACKER,
    hp: 2, gravity: false, stationary: false, moveSpeed: 65, patrolRange: 100,
    detectRange: 280, attackRange: 250, shootCooldown: 1600, projectileSpeed: 200,
    contactDamage: 1, bodySize: [22, 14], bodyOffset: [3, 3], flightBob: true
  },
  'drone-alert': {
    texture: TEXTURE_KEYS.DRONE_ALERT,
    hp: 2, gravity: false, stationary: false, moveSpeed: 80, patrolRange: 140,
    detectRange: 320, attackRange: 220, shootCooldown: 1400, projectileSpeed: 240,
    contactDamage: 1, bodySize: [22, 14], bodyOffset: [3, 3], flightBob: true
  },
  */

  turret: {
    texture: TEXTURE_KEYS.TURRET,
    // The art is a ceiling-mount sentry (flat bracket at the top, barrel
    // hanging below) — sized down from the walking-enemy scales since it
    // reads as bulky sitting under a thin platform, and positioned via
    // ceilingMount (see the constructor) instead of standing on a surface.
    scale: 0.4,
    ceilingMount: true,
    hp: 4,
    gravity: false,
    stationary: true,
    moveSpeed: 0,
    patrolRange: 0,
    detectRange: 260,
    attackRange: 260,
    shootCooldown: 1200,
    projectileSpeed: 260,
    contactDamage: 0,
    bodySize: [22, 24],
    contentBBox: [0, 15, 94, 109]
  },
  'corp-enforcer': {
    texture: TEXTURE_KEYS.BOSS,
    scale: 1.053,
    hp: 28,
    gravity: true,
    stationary: false,
    moveSpeed: 65,
    patrolRange: 0,
    detectRange: 999999,
    attackRange: 320,
    shootCooldown: 850,
    projectileSpeed: 300,
    contactDamage: 2,
    bodySize: [62, 62],
    contentBBox: [16, 9, 192, 184],
    boss: true
  }
};

function buildStates(config) {
  return {
    idle: {
      enter() {
        this.body.setVelocity(0, 0);
      },
      update() {
        if (this.distanceToTarget() <= config.detectRange) this.fsm.setState('aggro');
      }
    },

    patrol: {
      update(fsm, dt) {
        const speed = config.moveSpeed;
        this.body.velocity.x = this.patrolDir * speed;
        if (config.flightBob) {
          this._flightPhase += dt * 0.003;
          this.y = this.originY + Math.sin(this._flightPhase) * 10;
          // Manually moving a physics-enabled sprite doesn't move its body —
          // without this, the body (and therefore hit detection) stays
          // frozen at spawn height while the sprite visibly bobs, which
          // reads as flicker/duplication once the two drift apart.
          this.body.updateFromGameObject();
        }
        if (this.x > this.originX + config.patrolRange) this.patrolDir = -1;
        else if (this.x < this.originX - config.patrolRange) this.patrolDir = 1;
        this.setFlipX(this.patrolDir < 0);

        if (this.distanceToTarget() <= config.detectRange) this.fsm.setState('aggro');
      }
    },

    aggro: {
      enter() {
        this._lostTimer = 0;
      },
      update(fsm, dt) {
        const dist = this.distanceToTarget();
        if (dist > config.detectRange * 1.5) {
          this._lostTimer += dt;
          if (this._lostTimer > 1500) {
            this.fsm.setState(config.stationary ? 'idle' : 'patrol');
            return;
          }
        } else {
          this._lostTimer = 0;
        }

        if (!config.stationary && this.target) {
          const dir = this.target.x > this.x ? 1 : -1;
          this.body.velocity.x = dir * config.moveSpeed * (config.boss ? 1.4 : 1);
          this.setFlipX(dir < 0);
          if (config.flightBob) {
            // Deliberately no continuous sine wobble here (unlike patrol) —
            // a lerp chasing a perpetually-moving target never settles,
            // which read as the drone being permanently stuck oscillating.
            // Hover smoothly at a fixed offset above the player instead.
            const maxY = this.scene.levelData ? this.scene.levelData.heightPx - 96 : Infinity;
            const desiredY = Math.min(this.target.y - 40, maxY);
            this.y = Phaser.Math.Linear(this.y, desiredY, 0.05);
            this.body.updateFromGameObject();
          }
        } else {
          this.body.velocity.x = 0;
        }

        if (dist <= config.attackRange && (!config.requiresLineOfSight || this.hasLineOfSight())) {
          this.fsm.setState('attack');
        }
      }
    },

    attack: {
      enter() {
        this._shootTimer = Math.min(this._shootTimer, 200);
      },
      update(fsm, dt) {
        const dist = this.distanceToTarget();

        // If the player moves out of alignment/behind cover after we've
        // already entered attack, go back to repositioning instead of
        // freezing in place forever unable to take the shot.
        if (config.requiresLineOfSight && !this.hasLineOfSight()) {
          this.fsm.setState('aggro');
          return;
        }

        if (!config.stationary) {
          this.body.velocity.x = 0;
        }
        if (this.target) this.setFlipX(this.target.x < this.x);

        // Without this, a flying enemy's height just freezes the instant it
        // enters attack (unlike patrol/aggro, which both manage it), then
        // jumps again whenever it briefly re-enters aggro — reads as being
        // stuck oscillating vertically while pinned horizontally.
        if (config.flightBob && this.target) {
          const maxY = this.scene.levelData ? this.scene.levelData.heightPx - 96 : Infinity;
          const desiredY = Math.min(this.target.y - 40, maxY);
          this.y = Phaser.Math.Linear(this.y, desiredY, 0.05);
          this.body.updateFromGameObject();
        }

        this._shootTimer -= dt;
        if (this._shootTimer <= 0) {
          if (!config.requiresLineOfSight || this.hasLineOfSight()) {
            this.shootAtTarget();
            this._shootTimer = config.shootCooldown + Phaser.Math.Between(-100, 150);
          } else {
            this._shootTimer = 150; // blocked — recheck soon instead of waiting a full cooldown
          }
        }

        if (dist > config.attackRange * 1.25) this.fsm.setState('aggro');
      }
    }
  };
}

export class Enemy extends Phaser.Physics.Arcade.Sprite {
  constructor(scene, x, y, type, { projectilePool, target } = {}) {
    const config = ENEMY_CONFIGS[type];
    if (!config) throw new Error(`[Enemy] Unknown enemy type: ${type}`);
    super(scene, x, y, config.texture);
    scene.add.existing(this);
    scene.physics.add.existing(this);

    this.type = type;
    this.config = config;
    this.projectilePool = projectilePool;
    this.target = target;

    this.setDepth(DEPTHS.ENEMIES);
    const scale = config.scale ?? 1;
    if (config.scale) this.setScale(scale);
    // Body.setSize()/setOffset() take NATIVE (pre-scale) pixels — Phaser
    // multiplies both by the current scale automatically every physics
    // step (verified against Phaser's own source). bodySize is authored in
    // the actual intended on-screen pixels, so it has to be divided by
    // `scale` here before handing it to Phaser.
    const [bodyW, bodyH] = config.bodySize;
    const sourceW = bodyW / scale;
    const sourceH = bodyH / scale;
    // Offset is derived from contentBBox (native pixels, measured from the
    // source PNG) instead of a fixed number — see the comment above
    // ENEMY_CONFIGS for why a fixed offset breaks as soon as `scale`
    // changes. Flying enemies (flightBob) center the box on the content;
    // everything else (grounded, or turret sitting on a surface)
    // bottom-aligns it with the lowest drawn pixel — the character's feet.
    const [x0, y0, x1, y1] = config.contentBBox;
    const centerX = (x0 + x1) / 2;
    const offsetX = centerX - sourceW / 2;
    const offsetY = config.flightBob ? (y0 + y1) / 2 - sourceH / 2 : y1 - sourceH;
    this.body.setSize(sourceW, sourceH).setOffset(offsetX, offsetY);

    // Chunk data spawns entities at a tile-row CENTER and always assumed a
    // roughly 32px-tall sprite (GROUND_ROW/TIER rows are placed exactly 1
    // row above the surface they stand on, i.e. "center + 16px = surface").
    // That assumption breaks badly for the much bigger comic-art sprites —
    // e.g. a soldier's content bbox puts its feet ~45-125px below its
    // center at these scales, not 16px — so without this correction, a
    // grounded enemy spawns with its feet computed well below the actual
    // floor, deeply embedded in solid ground. Arcade Physics doesn't
    // resolve a body that starts already overlapping solid ground the way
    // it resolves a body that collides while moving, so it just falls
    // through forever instead of landing. This repositions the sprite at
    // spawn so its REAL feet (from contentBBox) land exactly where the
    // tile-grid convention intended (16px below the spawn coordinate),
    // instead of the sprite's raw center landing there.
    //
    // ceilingMount (turret) is the mirror case: its chunk row is one tile
    // BELOW the platform it hangs from (see generate-chunks.mjs), so the
    // "surface" it aligns to is 16px ABOVE the spawn coordinate, and it's
    // the content's TOP (the mounting bracket, y0) that has to land there —
    // not the bottom. That "surface" also isn't a full tile's edge: the
    // platform texture itself is only 40% of a tile tall and centered in
    // its row (TILE_PLATFORM in textures.js, placed in Game.js's
    // _buildTiles), so its real underside sits above the row's full bottom
    // edge by CEILING_GAP — without this, the turret hung visibly below
    // the platform's actual drawn edge.
    if (config.ceilingMount) {
      this.y = y - 16 - CEILING_GAP - scale * (y0 - this.height / 2);
    } else if (!config.flightBob) {
      this.y = y + 16 - scale * (y1 - this.height / 2);
    }

    if (!config.gravity) this.body.setAllowGravity(false);
    if (config.stationary) this.body.setImmovable(true);

    this.setData('team', 'enemy');
    this.setData('invulnerable', false);
    this.hp = config.hp;
    this.maxHp = config.hp;
    this.alive = true;
    this.originX = x;
    this.originY = y;
    this.patrolDir = Math.random() < 0.5 ? -1 : 1;
    this._shootTimer = Phaser.Math.Between(200, config.shootCooldown);
    this._flightPhase = Math.random() * Math.PI * 2;
    this._lostTimer = 0;

    this.fsm = new EnemyStateMachine(this, buildStates(config), config.stationary ? 'idle' : 'patrol');
  }

  distanceToTarget() {
    return this.target ? Phaser.Math.Distance.Between(this.x, this.y, this.target.x, this.target.y) : Infinity;
  }

  takeDamage(amount) {
    if (!this.alive) return;
    this.hp -= amount;
    this.setTintFill(0xffffff);
    this.scene.time.delayedCall(60, () => {
      if (this.active) this.clearTint();
    });
    this.scene.events.emit('enemy-hp-changed', this);
    if (this.hp <= 0) this.die();
  }

  die() {
    if (!this.alive) return;
    this.alive = false;
    this.setData('team', 'dead');
    this.body.enable = false;
    this.scene.events.emit('enemy-died', this);
    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      // Relative to this enemy's own base scale (which now varies per type
      // since the comic-art overhaul), not an absolute 0.6 — otherwise a
      // small-scaled enemy (e.g. scale 0.28) would visibly jump BIGGER
      // right as it dies instead of shrinking.
      scale: this.scale * 0.6,
      duration: 220,
      onComplete: () => this.destroy()
    });
  }

  /** Samples points along the line to the target against the tile grid — no alignment requirement, since shots can go either horizontal or vertical. */
  hasLineOfSight() {
    if (!this.target || !this.scene.levelData) return true;

    const { rows, tileSize } = this.scene.levelData;
    const steps = 20;
    for (let i = 1; i < steps; i++) {
      const t = i / steps;
      const x = Phaser.Math.Linear(this.x, this.target.x, t);
      const y = Phaser.Math.Linear(this.y, this.target.y, t);
      const col = Math.floor(x / tileSize);
      const row = Math.floor(y / tileSize);
      if (rows[row] && rows[row][col] === '#') return false;
    }
    return true;
  }

  shootAtTarget() {
    if (!this.projectilePool || !this.target) return;
    // Fires along whichever axis is dominant toward the target — a clean
    // horizontal or vertical beam (never diagonal), chosen by whichever
    // offset is larger, so every enemy can hit a target beside OR above/
    // below it instead of only ever aiming one fixed way.
    const dx = this.target.x - this.x;
    const dy = this.target.y - this.y;
    let vx = 0;
    let vy = 0;
    if (Math.abs(dx) >= Math.abs(dy)) {
      vx = Math.sign(dx || 1) * this.config.projectileSpeed;
    } else {
      vy = Math.sign(dy || 1) * this.config.projectileSpeed;
    }
    this.projectilePool.fire(this.x, this.y, vx, vy, { damage: this.config.boss ? 2 : 1 });
    this.scene.events.emit('enemy-shot', this);
  }

  update(time, delta) {
    if (!this.alive) return;
    this.fsm.update(delta);

    // Universal safety net, independent of which state moved it: a flying
    // enemy's manually-set y should never end up at/below the floor line,
    // regardless of patrol origin, chase target, or future state additions.
    if (this.config.flightBob && this.scene.levelData) {
      const maxY = this.scene.levelData.heightPx - 96;
      if (this.y > maxY) {
        this.y = maxY;
        this.body.updateFromGameObject();
      }
    }
  }
}
